/**
 * Vermeulen-method free testosterone calculator.
 *
 * Source: Vermeulen A, Verdonck L, Kaufman JM. JCEM 84(10):3666-3672 (1999).
 *   https://pubmed.ncbi.nlm.nih.gov/10523012/
 *
 * Mirrors the canonical JS implementation in
 * `andrew-marsmen/TestosteroneCalculator` (free-testosterone-calculator.html).
 * Quadratic-equation form solved for free testosterone in mol/L, then converted
 * back to clinical units.
 */

const KA_SHBG = 1.0e9;
const KA_ALBUMIN = 3.6e4;
const ALBUMIN_MW_G_PER_MOL = 66430;
const TOTAL_T_NMOLL_PER_NGDL = 1 / 28.842;
const FREE_T_PGML_PER_NMOLL = 288.42;
const FREE_T_NGDL_PER_NMOLL = 28.842;

export interface VermeulenInput {
  totalTngdl: number;
  shbgNmolL: number;
  albuminGdl: number;
}

export interface VermeulenResult {
  freeTpgml: number;
  freeTngdl: number;
  freeTnmolL: number;
  bioTngdl: number;
  pctFree: number;
  pctAlbumin: number;
  pctShbg: number;
}

function assertFinitePositive(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} must be a finite positive number; got ${value}`);
  }
}

export function calculateVermeulen(input: VermeulenInput): VermeulenResult {
  assertFinitePositive(input.totalTngdl, "totalTngdl");
  assertFinitePositive(input.shbgNmolL, "shbgNmolL");
  assertFinitePositive(input.albuminGdl, "albuminGdl");

  const totalTnmolL = input.totalTngdl * TOTAL_T_NMOLL_PER_NGDL;
  const T = totalTnmolL * 1e-9;
  const S = input.shbgNmolL * 1e-9;
  const A = (input.albuminGdl * 10) / ALBUMIN_MW_G_PER_MOL;

  const Ks = KA_SHBG;
  const Ka = KA_ALBUMIN;

  const a = Ks * (1 + Ka * A);
  const b = 1 + Ka * A + Ks * (S - T);
  const c = -T;

  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) {
    throw new Error("Vermeulen quadratic has no real solution for given inputs");
  }

  const freeTmol = (-b + Math.sqrt(discriminant)) / (2 * a);
  if (freeTmol <= 0) {
    throw new Error("Vermeulen quadratic produced non-positive free testosterone");
  }

  const freeTnmolL = freeTmol * 1e9;
  const albuminBoundTnmolL = freeTmol * Ka * A * 1e9;
  const shbgBoundTnmolL = totalTnmolL - freeTnmolL - albuminBoundTnmolL;

  const freeTpgml = freeTnmolL * FREE_T_PGML_PER_NMOLL;
  const freeTngdl = freeTnmolL * FREE_T_NGDL_PER_NMOLL;
  const bioTngdl = (freeTnmolL + albuminBoundTnmolL) * FREE_T_NGDL_PER_NMOLL;

  return {
    freeTpgml,
    freeTngdl,
    freeTnmolL,
    bioTngdl,
    pctFree: (freeTnmolL / totalTnmolL) * 100,
    pctAlbumin: (albuminBoundTnmolL / totalTnmolL) * 100,
    pctShbg: (shbgBoundTnmolL / totalTnmolL) * 100,
  };
}
